<wcg-profile-card [size]="cardSize()" data-testid="profile-info-wrapper__card">
  <ng-container wcgProfileCardHeader>
    <h2
      class="flex flex-row items-center gap-4 profile-info__title"
      data-testid="profile-info-wrapper__title"
    >
      <mat-icon fontSet="material-icons-outlined" class="!text-blue-11"
      >account_circle</mat-icon
      >
      {{ title() }}
    </h2>
  </ng-container>

  <!-- Edit button only visible when user has edit permissions and data is done loading -->

  @if (canEdit()) {
    <ng-container wcgProfileCardHeaderButton>
      @if (!editState()) {
        <wcg-button
          color="tertiary"
          (click)="editState.set(true)"
          data-testid="profile-info-wrapper__edit-button"
        >
          Edit
        </wcg-button>
      }
      @if (editState()) {
        <div class="flex flex-row gap-4">
          <wcg-button
            color="tertiary"
            (click)="handleCancel()"
            data-testid="profile-info-wrapper__cancel-button"
          >
            Cancel
          </wcg-button>
          <wcg-button
            color="primary"
            (click)="saveProfileInfo()"
            data-testid="profile-info-wrapper__save-button"
            [isDisabled]="profileInfoForm().invalid()"
          >
            Save
          </wcg-button>
        </div>
      }
    </ng-container>
  }
  <!-- Indentifier to show if profile belongs to an internal user or external user.
  This is based on the role of the user, if the role is internal then it will show
  internal otherwise it will show external. This is used to conditionally show/hide
  the QC Function field -->
  @let isUserInternal = profileInfoForm.role().value()?.isInternal;

  <ng-container wcgProfileCardContent>
    @if (!loading()) {
      <wcg-inline-editor
        [turnoffInlineEdit]="false"
        [defaultView]="editState() ? 'Edit' : 'View'"
        data-testid="profile-info-wrapper__inline-editor"
      >
        <ng-template wcgInlineEditMode>
          <ng-container [ngTemplateOutlet]="editModeTemplateRef" />
        </ng-template>
        <ng-template wcgInlineViewMode>
          <ng-container [ngTemplateOutlet]="viewModeTemplateRef" />
        </ng-template>
      </wcg-inline-editor>
    } @else {
      <!-- Skeleton loader per section/field while profile info is loading -->
      @let backgroundColor = "#dde1e4";
      @let skeletonTheme =
        {
          "border-radius": "5px",
          height: "20px",
          width: "95%",
          "background-color": backgroundColor,
          "margin-bottom": "5px",
          "margin-top": "5px",
          marginRight: "8px",
          marginLeft: "12px",
        };
      <div class="w-full flex flex-col gap-4">
        <!-- Personal Details -->
        <div
          class="w-full flex flex-col gap-4 px-[24px] py-[16px] profile-info-wrapper__border-bottom"
        >
          <h2 class="profile-info-wrapper__sub-title">Personal Details</h2>
          <ngx-skeleton-loader
            count="7"
            appearance="line"
            [theme]="skeletonTheme"
          />
        </div>
        <!-- Role Information -->
        <div
          class="w-full flex flex-col gap-4 px-[24px] py-[16px] profile-info-wrapper__border-bottom"
        >
          <h2 class="profile-info-wrapper__sub-title">Role Information</h2>
          <ngx-skeleton-loader
            count="2"
            appearance="line"
            [theme]="skeletonTheme"
          />
        </div>
        @if (isInternal()) {
          <div class="w-full flex flex-col gap-4 px-[24px] py-[16px]">
            <h2 class="profile-info-wrapper__sub-title">System Ids</h2>
            <ngx-skeleton-loader
              count="2"
              appearance="line"
              [theme]="skeletonTheme"
            />
          </div>
        }
      </div>
    }
  </ng-container>
</wcg-profile-card>

<ng-template #viewModeUserAccessInfoTemplateRef>
  @for (
    ri of profileInfoForm.role().value() | keyvalue: originalOrder;
    track ri.key
    ) {
    @if (ri.key.toLowerCase() === "role" && typeof ri.value === "object") {
      <div class="w-full flex flex-row items-center gap-4">
        <div class="w-[30%] profile-info__body-label">
          <strong>{{ ri.key | labelCase }}</strong>
        </div>
        <div class="!w-[70%] profile-info__body-text !w-[70%]">
          <div
            class="flex flex-row items-center gap-4"
            id="profile-info__role-status"
          >
            <span>{{ ri.value?.label ?? "" }}</span>
            @let isConstrast = userStatusLabel().toLowerCase() === "active";
            <wcg-chip
              [elevated]="false"
              [label]="userStatusLabel()"
              [contrast]="isConstrast"
              [type]="getStatusType(userStatusLabel())"
              [size]="roleChipSize"
              class="mr-3"
            />
          </div>
        </div>
      </div>
    } @else if (
      ri.key.toLowerCase() === "qcfunction" &&
      typeof ri.value === "object" &&
      isUserInternal
      ) {
      <div class="w-full flex flex-row items-center gap-4">
        <div class="w-[30%] profile-info__body-label">
          <strong>{{ ri.key | labelCase }}</strong>
        </div>
        <div class="!w-[70%] profile-info__body-text !w-[70%] break-words">
          {{ ri.value?.label ?? "" }}
        </div>
      </div>
    }
  }
</ng-template>

<!-- profile in view mode -->
<ng-template #viewModeTemplateRef>
  <div
    class="w-full flex flex-col gap-4"
    data-testid="profile-info-wrapper__view-mode"
  >
    <!-- Personal Details -->
    <div
      class="w-full flex flex-col gap-4 px-[24px] py-[16px] profile-info-wrapper__border-bottom"
      data-testid="profile-info-wrapper__personal-details"
    >
      <h2
        class="profile-info-wrapper__sub-title"
        data-testid="profile-info-wrapper__personal-details-title"
      >
        Personal Details
      </h2>
      <div class="w-full flex flex-col gap-4">
        @let exceptions =
          { titleCaseWords: ["timeZone"], capitalCaseWords: ["npi"] };
        @for (
          pd of profileInfoForm.personalDetails().value()
            | keyvalue: originalOrder;
          track pd.key
          ) {
          @if (
            !["isPi", "email", "id", "prefix", "legacyId"].includes(pd.key)
            ) {
            <div class="w-full flex flex-row items-center gap-4">
              <div class="w-[30%] profile-info__body-label">
                <strong>{{ pd.key | labelCase: exceptions }}</strong>
                @if (["firstName", "lastName"].includes(pd.key)) {
                  <span
                    class="text-red-12"
                    [attr.data-testid]="
                      `profile-info-wrapper__required-asterisk-${pd.key}`
                    "
                  >*</span
                  >
                }
              </div>
              @if (
                !["isPi", "email", "id", "prefix", "legacyId"].includes(pd.key)
                ) {
                <div class="!w-[70%] profile-info__body-text break-words">
                  {{ pd.value }}
                </div>
              }
            </div>
          }
        }
      </div>
    </div>

    <!-- Role Information -->
    <div
      class="w-full flex flex-col gap-4 px-[24px] py-[16px] profile-info-wrapper__border-bottom"
      data-testid="profile-info-wrapper__role-info"
    >
      <h2
        class="profile-info-wrapper__sub-title"
        data-testid="profile-info-wrapper__role-info-title"
      >
        Role Information
      </h2>
      <div class="w-full flex flex-col gap-4">
        <ng-container *ngTemplateOutlet="viewModeUserAccessInfoTemplateRef" />
      </div>
    </div>

    <!-- System IDs -->
    @if (isInternal()) {
      <div
        class="w-full flex flex-col gap-4 px-[24px] py-[16px]"
        data-testid="profile-info-wrapper__system-ids"
      >
        <h2
          class="profile-info-wrapper__sub-title"
          data-testid="profile-info-wrapper__system-ids-title"
        >
          System Ids
        </h2>
        <div class="w-full flex flex-col gap-4">
          @for (
            si of profileInfoForm.systemIds().value() | keyvalue: originalOrder;
            track si.key
            ) {
            @let _excludeSystemKeys = ["oktaId", "investigatorWcgId"];
            @if(!_excludeSystemKeys.includes(si.key)) {
            <div
              class="w-full flex flex-row items-center gap-4"
              attr.data-testid="profile-info-wrapper__system-id-{{ si.key }}"
            >
              <div class="w-[30%] profile-info-wrapper__body-label">
                <strong
                  attr.data-testid="profile-info-wrapper__system-id-label-{{
                    si.key
                  }}"
                >{{ si.key | labelCase }}</strong
                >
              </div>
              <ng-container
                [ngTemplateOutlet]="DefaultSystemIdRef"
                [ngTemplateOutletContext]="{
                  props: {
                    key: si.key,
                    value: si.value,
                  },
                }"
              />
            </div>
            }
          }
        </div>
      </div>
    }
  </div>
</ng-template>

<!-- profile in edit mode -->
<ng-template #editModeTemplateRef>
  <div class="w-full flex flex-col gap-4">
    <!--Personal Details Start-->
    <div
      class="w-full flex flex-col gap-4 px-[24px] py-[16px] profile-info__border-bottom"
    >
      <h2 class="profile-info__sub-title">Personal Details</h2>
      <div class="w-full flex flex-col gap-[2.5rem]">
        @let exceptions =
          {
            titleCaseWords: ["timeZone"],
            capitalCaseWords: ["npi"],
          };
        @for (
          pd of profileInfoForm.personalDetails().value()
            | keyvalue: originalOrder;
          track pd.key
          ) {
          @if (
            !["isPi", "email", "id", "prefix", "legacyId"].includes(pd.key)
            ) {
            <div class="w-full flex flex-row items-center gap-4 !py-[24x]">
              <div class="w-[30%] profile-info__body-label">
                <strong>{{ pd.key | labelCase: exceptions }}</strong>
                @if (["firstName", "lastName"].includes(pd.key)) {
                  <span
                    class="text-red-12"
                    [attr.data-testid]="
                      `profile-info-wrapper__required-asterisk-${pd.key}`
                    "
                  >*</span
                  >
                }
              </div>

              @switch (pd.key) {
                @case ("firstName")
                @case ("middleName")
                @case ("lastName"){
                  <ng-container
                    [ngTemplateOutlet]="DefaultSystemIdRef"
                    [ngTemplateOutletContext]="{
                    props: {
                      key: pd.key,
                      value: pd.value,
                    },
                  }"
                  />
                }
                @case ("suffix") {
                  <mat-form-field
                    class="wrapper-select !w-[70%]"
                    appearance="outline"
                  >
                    <mat-select
                      placeholder="Select Suffix"
                      [formField]="profileInfoForm.personalDetails.suffix!"
                    >
                      @for (suffix of suffixes; track suffix) {
                        <mat-option [value]="suffix">{{ suffix }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
                @case ("pronouns") {
                  <mat-form-field
                    class="wrapper-input !w-[70%]"
                    appearance="outline"
                  >
                    <input
                      matInput
                      placeholder="Enter Pronouns"
                      [formField]="profileInfoForm.personalDetails.pronouns!"
                    />
                  </mat-form-field>
                }
                @case ("timeZone") {
                  <div class="profile-info__body-text !w-[70%] break-words">
                    {{
                      profileInfoForm.personalDetails.timeZone().value() ?? ""
                    }}
                  </div>
                }
                @case ("npi") {
                  <mat-form-field
                    class="wrapper-input !w-[70%]"
                    appearance="outline"
                  >
                    <input
                      matInput
                      placeholder="Enter NPI"
                      [formField]="profileInfoForm.personalDetails.npi"
                    />
                    @for (
                      error of profileInfoForm.personalDetails.npi().errors();
                      track error.kind
                      ) {
                      <mat-error class="min-h-4">{{ error.message }}</mat-error>
                    }
                  </mat-form-field>
                }
              }
            </div>
          }
        }
      </div>
    </div>
    <!-- !Personal Details End-->
    <!--Role Information Start-->
    <div
      class="w-full flex flex-col gap-4 px-[24px] py-[16px]"
      [ngClass]="{ 'profile-info__border-bottom': canEdit() }"
    >
      <h2 class="profile-info__sub-title">Role Information</h2>
      <div class="w-full flex flex-col gap-4">
        @if (
          (canEditAttribute() || canEditRole() || canEditStatus()) && !isStaff()
        ) {
          @for (
            ri of profileInfoForm.role().value() | keyvalue: originalOrder;
            track ri.key
            ) {
            <div class="w-full flex flex-row items-center gap-4">
              @if (
                ri.key.toLowerCase() === "role" ||
                (ri.key.toLowerCase() === "qcfunction" && isUserInternal)
                ) {
                <div class="w-[30%] profile-info__body-label">
                  <strong>{{ ri.key | labelCase }}</strong>
                </div>
              }
              <div class="profile-info__body-text !w-[70%]">
                @switch (ri.key.toLowerCase()) {
                  @case ("role") {
                    <div class="flex flex-row items-center gap-4">
                      <!-- Show dropdown if user is internal otherwise show text -->
                      @if (isUserInternal) {
                        <mat-form-field
                          class="wrapper-select !w-[70%]"
                          appearance="outline"
                        >
                          <mat-select
                            placeholder="Select Role"
                            [compareWith]="compareObjectsFn"
                            [formField]="profileInfoForm.role.role"
                          >
                            @for (role of roles(); track role.id) {
                              <mat-option [value]="role">{{
                                  role.label
                                }}</mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      } @else {
                        <!-- Otherwise just show the role label for external users since role is an internal attribute -->
                        @if ( typeof ri.value === "object") {
                          <span>{{ ri.value?.label ?? ''}}  </span>
                        }
                      }
                      <wcg-switch-new
                        name="statusToggle"
                        size="slim"
                        variant="icon"
                        [disabled]="!canEditStatus()"
                        [ngModel]="isUserActive()"
                        (ngModelChange)="onStatusToggle($event)"
                        data-testid="profile-info-wrapper__status-toggle"
                      >
                        {{ isUserActive() ? "Active" : "Inactive" }}
                      </wcg-switch-new>
                    </div>
                  }
                  @case ("qcfunction") {
                    <!-- Only show QC Function field if user is internal since it's an internal attribute -->
                    @if (isUserInternal) {
                      <mat-form-field
                        class="wrapper-select !w-[70%]"
                        appearance="outline"
                      >
                        <mat-select
                          placeholder="Select QC Function"
                          [compareWith]="compareObjectsFn"
                          [formField]="profileInfoForm.role.qcFunction"
                        >
                          @for (
                            qcFunction of smeqcAttributes();
                            track qcFunction.id
                            ) {
                            <mat-option [value]="qcFunction">{{
                                qcFunction.label
                              }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                  }
                }
              </div>
            </div>
          }
        } @else {
          <ng-container *ngTemplateOutlet="viewModeUserAccessInfoTemplateRef" />
        }
      </div>
    </div>
    <!--Role Information End-->
    <!-- System Ids Start-->
    @if (isInternal()) {
      <div class="w-full flex flex-col gap-4 px-[24px] py-[16px]">
        <h2 class="profile-info__sub-title">System Ids</h2>
        <div class="w-full flex flex-col gap-[3rem]">
          @for (
            si of profileInfoForm.systemIds().value() | keyvalue: originalOrder;
            track si.key
            ) {
            @let _excludeSystemKeys = ["oktaId", "investigatorWcgId"];
            @if(!_excludeSystemKeys.includes(si.key)) {
              <div class="w-full flex flex-row items-center gap-4">
              <div class="w-[30%] profile-info__body-label">
                <strong>{{ si.key | labelCase }}</strong>
              </div>
              @if (canEdit() && !isStaff()) {
                @switch (si.key) {
                  @case ("primaryContactId") {
                    <mat-form-field
                      class="wrapper-input !w-[70%]"
                      appearance="outline"
                    >
                      <input
                        matInput
                        placeholder="Enter Primary Contact Id"
                        [formField]="
                          profileInfoForm.systemIds.primaryContactId!
                        "
                      />

                      @for (
                        error of profileInfoForm.systemIds
                          .primaryContactId()
                          .errors();
                        track error.kind
                        ) {
                        <mat-error class="min-h-4">{{
                            error.message
                          }}</mat-error>
                      }
                    </mat-form-field>
                  }
                  @case ("additionalContactIds") {
                    <mat-form-field
                      class="wrapper-input !w-[70%]"
                      appearance="outline"
                    >
                      <input
                        matInput
                        placeholder="Enter Additional Contact Ids (comma separated)"
                        [formField]="
                          profileInfoForm.systemIds.additionalContactIds!
                        "
                      />
                      @for (
                        error of profileInfoForm.systemIds
                          .additionalContactIds()
                          .errors();
                        track error.kind
                        ) {
                        <mat-error class="min-h-4">{{
                            error.message
                          }}</mat-error>
                      }
                    </mat-form-field>
                  }
                  @default {
                    <ng-container
                      [ngTemplateOutlet]="DefaultSystemIdRef"
                      [ngTemplateOutletContext]="{
                        props: {
                          key: si.key,
                          value: si.value,
                        },
                      }"
                    />
                  }
                }
              } @else {
                <ng-container
                  [ngTemplateOutlet]="DefaultSystemIdRef"
                  [ngTemplateOutletContext]="{
                    props: {
                      key: si.key,
                      value: si.value,
                    },
                  }"
                />
              }
            </div>
            }
          }
        </div>
      </div>
    }
    <!-- System Ids End-->
  </div>
</ng-template>

<!--Default System Ids Ref Start-->
<ng-template
  #DefaultSystemIdRef
  [typeValidator]="defaultSystemIdsRef"
  let-props="props"
>
  <div
    class="profile-info__body-text !w-[70%] break-words"
    attr.data-testid="profile-info-wrapper__system-id-value-{{ props.key }}"
  >
    @if (props.key.toLowerCase() === "userid") {
      {{ profileInfoForm.personalDetails.email().value() }}
    } @else {
      {{ props.value }}
    }
  </div>
</ng-template>
